import React, { useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import { Num, Ring, Spark, useReady } from "../admin/adminCharts";
import { ROUTES } from "../shared/classData";
import useWidgetLayout from "../widgets/useWidgetLayout";
import { CustomizeButtons, EditBar, SaveErrorBar, WidgetGrid, WidgetLibrary } from "../widgets/WidgetGrid";
import { COORDINATOR_DEFAULTS, COORDINATOR_WIDGETS } from "./coordinatorWidgets";
import "./coordinatorDashboard.css";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function Hero({ data, ready, L }) {
  const { header, kpis, empty, navigate } = data;
  const tiles = [
    { l: "Total enrollment", v: kpis.enrollment, s: empty ? "No learners enrolled yet" : "Across active cohorts", viz: null },
    { l: "Active cohorts", v: kpis.cohorts, s: empty ? "No cohorts running" : kpis.startsLater ? `${plural(kpis.startsLater, "cohort")} start${kpis.startsLater === 1 ? "s" : ""} ${kpis.nextStart ? `from ${kpis.nextStart}` : "soon"}` : "No upcoming starts",
      viz: <div className="mini" aria-hidden="true">{kpis.cohortMini.map((m, i) => <i key={i} className={i === kpis.cohortMini.length - 1 ? "on" : ""} style={{ height: ready ? `${m / Math.max(1, ...kpis.cohortMini) * 100}%` : 0, transitionDelay: `${i * 70}ms` }}></i>)}</div> },
    { l: "Instructor certs · 90 days", v: kpis.certs, s: empty ? "Nothing to track yet" : <><span className="dl w">{kpis.certsSoon}</span>within 30 days</>,
      viz: <Spark data={kpis.certsSpark} color="var(--color-accent-pink)" w={96} h={32} delay={420} /> },
    { l: "Checklist pass rate", v: kpis.passRate, dec: 1, suf: "%", s: kpis.passRate == null ? "No checklists graded" : `Across ${kpis.graded} graded sheets`,
      viz: kpis.passRate != null ? <Ring pct={kpis.passRate} size={44} sw={5} color="var(--color-accent-lime)" delay={350} /> : null },
  ];
  return (
    <header className="cd-hero" data-testid="coordinator-hero"><div className="cd-hero-in">
      <div className="cd-top">
        <div className="t"><span className="eb">{header.eyebrow}</span>
          <h1>{header.greeting}</h1>
          <p>{empty ? "Your training center is set up. Add a course to start scheduling." : <><b>{plural(header.sessionsToday, "session")}</b> today · <b>{plural(header.itemsNeedAttention, "item")}</b> need attention</>}</p></div>
        {L.edit ? <CustomizeButtons L={L} dark /> : <>
          <button type="button" className="cd-btn dk" onClick={() => navigate(ROUTES.manageCourses)}><CalendarIcon sx={{ fontSize: 16 }} />Schedule session</button>
          <button type="button" className="cd-btn inv" onClick={() => navigate(ROUTES.manageCourses)}>New cohort</button>
          <CustomizeButtons L={L} dark /></>}
      </div>
      <div className="hm4">{tiles.map((k, i) => {
        const muted = k.v == null || (empty && !k.suf);
        return <div className="hk" key={k.l} style={{ animationDelay: `${i * 60}ms` }}>
          <span className="eb">{k.l}</span>
          <div className="row"><span className={`v${muted ? " mut" : ""}`}>{k.v == null ? "—" : empty ? "0" : <Num to={k.v} dec={k.dec || 0} suf={k.suf || ""} delay={i * 60} />}</span>{!empty && k.viz}</div>
          <span className="sub">{k.s}</span></div>;
      })}</div>
    </div></header>
  );
}

/**
 * Coordinator landing dashboard: a fixed KPI hero plus a customizable widget grid.
 * @param {{ data: object, raw: object, tenantId: string, aha?: object|null, now?: Date }} props
 *   `data` is the buildCoordinatorModel view model; `raw` the fetched courses / instructors / instructor certs.
 */
export default function CoordinatorDashboard({ data, raw, tenantId, aha = null, now: nowProp }) {
  const ready = useReady(300);
  const now = useMemo(() => nowProp || new Date(), [nowProp]);
  const L = useWidgetLayout({ role: "coordinator", tenantId, registry: COORDINATOR_WIDGETS, defaults: COORDINATOR_DEFAULTS });
  const ctx = useMemo(() => ({ m: data, raw, tenantId, aha, now, navigate: data.navigate }), [data, raw, tenantId, aha, now]);
  if (data.loading || L.loading) return <Box sx={{ display: "grid", placeItems: "center", minHeight: 240 }}><CircularProgress /></Box>;
  return (
    <div className="cd" data-testid="coordinator-dashboard">
      <Hero data={data} ready={ready} L={L} />
      <main className="cd-wrap">
        <div className="cd-grid">
          <SaveErrorBar L={L} />
          <EditBar L={L} />
          <WidgetGrid L={L} ctx={ctx} />
        </div>
      </main>
      <WidgetLibrary L={L} roleLabel="Coordinator" ctx={ctx} />
    </div>
  );
}
